/*
 * Title: Successfull/index.tsx
 * Author: Chris Rosa
 * Email: chris@encore.fans
 * Description: Successfully sign up popup.
 * Date: 30 Jun 2022
 * Copyright (c) Encore, 2022
 * Version:0.1.0
 */

import { useState, forwardRef, useRef } from "react";
import { toast } from "react-toastify";
import copy_check from "@assets/copy_check.png";
import { useSelector } from "react-redux";
import Api from "@services/api";
import Loader from "@components/Loader";
import { useDispatch } from "react-redux";
import NotificationService from "@services/notification-service";
import ProgramBox from "@components/ProgramBox";
import successGif from "@assets/lotties/success.json";
import Lottie from 'react-lottie'
import Mixpanel from "@services/mixpanel-events";

const Unlocked = forwardRef((props:any) => {
	const { actions } = require("@redux/ScoreRedux");
    const dispatch = useDispatch();
	const creator = useSelector((state: any) => state.creator);
	const [loader, setLoader] = useState(false);
	const inputRef = useRef(null);
	const userData = useSelector((state: any) => state.user?.user?.user);
	const program = useSelector((state: any) => state?.creator?.program);
	const score = useSelector((state: any) => state.score?.data);
	const programData = creator.program?.config?.data;
	const couponCode = programData?.tiers?.[props?.current -1]?.disc?.code;
	const fanId = userData?.id;
	const extraInfo = score?.extraInfo;
	
	const copyToClipboard = () => {
		Mixpanel.send("Coupon Copied", {coupon_code: couponCode})
		inputRef?.current.select();
		document.execCommand("copy");

		toast.success(
			<div className="flex items-center gap-x-3 text-[black] text-[14px]">
				<img
					alt="logo"
					src={copy_check}
					className="h-[20px] w-[20px] object-contain"
				/>
				Code copied!
			</div>,
			{
				icon: false,
				position: "bottom-center",
				autoClose: 2000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				theme: "light",
			}
		);
	};

	const handleNotify = async (key, value) => {
		setLoader(true);
		const data = {
			fanId: fanId,
			[key]: value,
		};
		const json = await Api.notifyMe(data);
		if(json?.status == 201){
			setLoader(false)
			actions.fetchScores(dispatch, program?.id);
		} else {
		NotificationService.error(json?.data?.message || "An error occurred")
        setLoader(false);
		}
	};

	const defaultOptions = {
		loop: true,
		autoplay: true,
		animationData: successGif,
		rendererSettings: {
		  preserveAspectRatio: "xMidYMid slice"
		}
	  };

	return (
		<div className="flex flex-col w-full items-center justify-center text-center">
			<div className="-mt-10">
				<Lottie options={defaultOptions} height={144} width={144} />
			</div>
			
			<h4 className="font-medium body-inlay-primary text-[24px] leading-8 tracking-wide text-center -mt-[12px]">
				Nice job!
			</h4>
			<p className="text-[16px] leading-[21.6px] mb-[28px] tracking-wide body-inlay-primary font-normal mt-[5px]">
				You unlocked <span className="font-semibold text-[16px] leading-[21.6px] tracking-wide body-inlay-primary">
					<ProgramBox box="DISCOUNT_DISCRIPTION" currentScreen={props?.current} />
				</span>
			</p>
			<div className="relative w-full">
				<div className="bg-tile w-full p-[15px_0px] transition-all duration-700 ease-in-out  rounded-[10px] relative overflow-hidden">
					<h6 className="font-normal text-[16px] tracking-wide leading-5 tile-inlay-primary opacity-80 text-center">
						Your promo code
					</h6>
					<div className="flex justify-center items-center mt-2">
						<h6 className="font-[500] text-[24px] leading-8 tracking-wide tile-inlay-primary text-center pr-3">
							{couponCode}
						</h6>
						<div className="cursor-pointer" onClick={copyToClipboard}>
						<svg width="21" height="21" className="copy-icon-color" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path d="M16.6667 17.1667H8.5C8.22386 17.1667 8 16.9428 8 16.6667V8.5C8 8.22386 8.22386 8 8.5 8H16.6667C16.9428 8 17.1667 8.22386 17.1667 8.5V16.6667C17.1667 16.9428 16.9428 17.1667 16.6667 17.1667Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
						<path d="M12.9997 7.99967V4.33301C12.9997 4.05687 12.7758 3.83301 12.4997 3.83301H4.33301C4.05687 3.83301 3.83301 4.05687 3.83301 4.33301V12.4997C3.83301 12.7758 4.05687 12.9997 4.33301 12.9997H7.99967" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
						</svg>
						</div>
						{/* working within an iframe */}
						<input
							ref={inputRef}
							value={couponCode}
							className="absolute left-[-9999px]"
							readOnly
						/>
						{/* working within an iframe */}
					</div>
				</div>
			</div>
			<div className="bg-[#fff]/20 lg:w-[390px] mt-10 mb-[6px] w-[360px] mx-auto h-[1px] rounded-full" />
			<ProgramBox isLink={true} box="POWERED_BY" title={"Earn with"} />
			<ProgramBox box="EMOJIS" />
			<div className="flex flex-col justify-center items-center mt-4">
				<p className="font-light leading-[21px] w-full max-w-[350px] mx-auto text-[16px] opacity-80 tracking-[0.02em] text-center body-inlay-primary">
					You just took the first step in building your Encore data backpack! Use it to save with more of your favorite retailers.
				</p>
				<a
					href="https://encore.fans"
					onClick={() => Mixpanel.send("Visited  Encore Website", {button_location: "Learn Button"})}
					target="_blank"
					className={`bg-btn btn-inlay-primary font-[400] p-[5px_12px_7px_12px] uppercase transition-all duration-300 ease-linear mt-4 flex items-center justify-center h-[35px] rounded-[50px] tracking-wider `}
				>
					learn more
				</a>
			</div>
		</div>
	);
});
export default Unlocked;
